import React from 'react';
import { Container, Typography, Box, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  Business,
  Public,
  TrendingUp,
  Group,
  Inventory,
  LocalShipping,
  SupportAgent,
  EmojiObjects,
  ThumbUp,
  MonetizationOn,
} from '@mui/icons-material';

const AmazonDropshippingProfile = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Company Name and Industry */}
        <Typography variant="h3" gutterBottom align="center" className="text-main-600 font-bold">
          Company Profile
        </Typography>
        <Typography variant="h5" gutterBottom align="center" sx={{ color: 'text.secondary' }}>
          Industry: E-Commerce | Website: blinkbuydropshipping.shop
        </Typography>

        {/* Overview Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Overview
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping is an innovative e-commerce platform that provides end-to-end dropshipping solutions for entrepreneurs and businesses worldwide. Our platform eliminates the need for physical inventory by connecting sellers with reliable suppliers who handle storage, packaging, and shipping directly to customers. We source high-quality products from leading global marketplaces including Amazon, AliExpress, Alibaba, Walmart, and eBay, offering sellers access to millions of verified products across multiple categories.
        </Typography>
        <Typography variant="body1" paragraph>
          With Blink Buy Dropshipping, you can focus on marketing, customer acquisition, and growing your brand while we manage the complex logistics of order fulfillment. Our platform provides automated tools, real-time analytics, wallet management, and dedicated support to help you build a profitable online business with minimal upfront investment.
        </Typography>
        <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', color: '#666', mt: 2 }}>
          <strong>Note:</strong> Blink Buy Dropshipping is not legally affiliated, endorsed, or partnered with Amazon, AliExpress, Alibaba, Walmart, or eBay. We source products from these public marketplaces through legitimate channels to provide our sellers with diverse inventory options.
        </Typography>

        {/* Mission and Vision Section */}
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom className="text-main-600 font-bold">
                <EmojiObjects sx={{ verticalAlign: 'middle', mr: 1, color: 'text-main-600' }} />
                Mission
              </Typography>
              <Typography variant="body1">
                To empower entrepreneurs and businesses with accessible, scalable dropshipping solutions that eliminate inventory risks and reduce operational costs. We provide comprehensive tools, supplier networks, and support systems that enable sellers to build profitable e-commerce businesses across multiple platforms and markets.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom className="text-main-600 font-bold">
                <Public sx={{ verticalAlign: 'middle', mr: 1, color: 'text-main-600' }} />
                Vision
              </Typography>
              <Typography variant="body1">
                To be the world's premier dropshipping platform, empowering millions of entrepreneurs to achieve financial independence through e-commerce. We aim to continuously innovate our technology, expand our supplier network, and provide unparalleled service that helps sellers succeed in the competitive digital marketplace.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Core Values Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Core Values
        </Typography>
        <Grid container spacing={4}>
          {coreValues.map((value, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ fontSize: '3rem', color: 'text-main-600' }}>
                  {value.icon}
                </Box>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {value.title}
                </Typography>
                <Typography variant="body1">{value.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Business Model Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Business Model
        </Typography>
        <List>
          {businessModel.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <TrendingUp fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>

        {/* Product Categories Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Product Categories
        </Typography>
        <Grid container spacing={4}>
          {productCategories.map((category, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {category}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Target Audience Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Target Audience
        </Typography>
        <List>
          {targetAudience.map((audience, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <Group fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={audience} />
            </ListItem>
          ))}
        </List>

        {/* Competitive Advantage Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Competitive Advantage
        </Typography>
        <Grid container spacing={4}>
          {competitiveAdvantages.map((advantage, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ fontSize: '3rem', color: 'text-main-600' }}>
                  {advantage.icon}
                </Box>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {advantage.title}
                </Typography>
                <Typography variant="body1">{advantage.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Key Services Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Key Services
        </Typography>
        <List>
          {keyServices.map((service, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <LocalShipping fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={service} />
            </ListItem>
          ))}
        </List>

        {/* Conclusion Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Conclusion
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping provides an accessible and profitable business opportunity for entrepreneurs looking to capitalize on the massive potential of the e-commerce industry. With a proven model, excellent customer service, and a seamless integration with Blink Buy's platform, it offers the flexibility and support needed to build and grow a successful online business. Whether you're new to e-commerce or an experienced seller, Blink Buy Dropshipping is an ideal solution to take your business to the next level.
        </Typography>
      </Box>
    </Container>
  );
};

// Core Values Data
const coreValues = [
  {
    icon: <EmojiObjects sx={{ color: 'text-main-600' }} />,
    title: 'Innovation',
    description: 'Continuously evolving with market trends to offer the most up-to-date solutions and products.',
  },
  {
    icon: <ThumbUp sx={{ color: 'text-main-600' }} />,
    title: 'Customer-Centric',
    description: 'Providing excellent customer service, hassle-free returns, and fast shipping to ensure satisfaction.',
  },
  {
    icon: <MonetizationOn sx={{ color: 'text-main-600' }} />,
    title: 'Integrity',
    description: 'Operating with transparency and honesty in every transaction and relationship.',
  },
  {
    icon: <TrendingUp sx={{ color: 'text-main-600' }} />,
    title: 'Efficiency',
    description: 'Optimizing business operations to reduce costs, improve delivery times, and maximize profits for entrepreneurs.',
  },
];

// Business Model Data
const businessModel = [
  'Multi-Platform Sourcing: Access products from Amazon, AliExpress, Alibaba, Walmart, and eBay through our integrated platform.',
  'Zero Inventory Risk: Sell products without purchasing or storing inventory. Suppliers handle all storage and logistics.',
  'Automated Order Management: Our platform automatically processes orders and coordinates with suppliers for direct customer shipping.',
  'Flexible Business Model: Start with low investment, scale at your own pace, and operate from anywhere in the world.',
  'Comprehensive Support: Business guidance, analytics tools, payment management, and dedicated customer service.',
];

// Product Categories Data
const productCategories = [
  'Electronics and gadgets',
  'Fashion and accessories',
  'Home and kitchen products',
  'Health and beauty',
  'Toys and games',
  'Sports and outdoor gear',
  'Office supplies and stationery',
];

// Target Audience Data
const targetAudience = [
  'Entrepreneurs: Individuals looking to build an online business with minimal upfront capital.',
  'Small Business Owners: Those seeking to expand their product offerings on Blink Buy.',
  'Retailers: Businesses looking for reliable fulfillment methods to reduce operational costs.',
];

// Competitive Advantages Data
const competitiveAdvantages = [
  {
    icon: <Business sx={{ color: 'text-main-600' }} />,
    title: 'Low Overhead',
    description: 'No need for warehousing or inventory management, keeping operational costs minimal.',
  },
  {
    icon: <Public sx={{ color: 'text-main-600' }} />,
    title: 'Global Reach',
    description: 'Access to millions of potential customers worldwide through Blink Buy.',
  },
  {
    icon: <Inventory sx={{ color: 'text-main-600' }} />,
    title: 'Automated Systems',
    description: 'Easy automation of order fulfillment for scalable business growth.',
  },
  {
    icon: <ThumbUp sx={{ color: 'text-main-600' }} />,
    title: 'Reputation & Trust',
    description: 'Leverage Blink Buy credibility to increase sales and reduce customer hesitation.',
  },
];

// Key Services Data
const keyServices = [
  'Dropshipping Setup: Guidance for setting up a successful Blink Buy dropshipping store.',
  'Supplier Sourcing: Assistance in finding trustworthy suppliers with competitive prices.',
  'Inventory Management: No need to stock inventory; purchase products after customer orders.',
  'Customer Support Solutions: Dedicated support for inquiries, returns, and shipment issues.',
  'Market Research & Trend Analysis: Tools to identify profitable products and niche markets.',
];

export default AmazonDropshippingProfile;